import React, { useEffect , useState, useCallback } from "react";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './BookNookPage.css';

import { API_BASE_URL, MAIN_SHELF_TYPES } from "../../Variables";
import BookDisplay from "../../components/BookDisplay";
import CurrentlyReadingDisplay from "../../components/CurrentlyReadingDisplay";
import CreateShelfModal from "../../components/CreateShelfModal";
import BookDetailsModal from "../../components/BookDetailsModal";
import CurrentlyReadingDetailsModal from "../../components/CurrentlyReadingDetailsModal";
import ConfirmationModal from "../../components/ConfirmationModal";

function BookNookPage(){
  // Token and Navigation
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  // Main Shelf States
  const [shelfLoading, setShelfLoading] = useState({}); // Loading state per main shelf
  const [shelfErrors, setShelfErrors] = useState({}); // Error state per main shelf
  const [shelfBooks, setShelfBooks] = useState({
    'Currently Reading': [], 
    'Want To Read': [], 
    'Read': [], 
    'Did Not Finish': []
  }); // Books for each main shelf

  // Custom Shelf States
  const [customShelves, setCustomShelves] = useState([]); // List of custom shelves
  const [customShelfLoading, setCustomShelfLoading] = useState({}); // Loading state per custom shelf
  const [customShelfErrors, setCustomShelfErrors] = useState({}); // Error state per custom shelf
  const [customShelfBooks, setCustomShelfBooks] = useState({}); // Books for each custom shelf

  // Modal and UI States
  const [displayCreateModal, setDisplayCreateModal] = useState(false);
  const [displayBookDetailsModal, setDisplayBookDetailsModal] = useState(false);
  const [displayCurrentlyReadingModal, setDisplayCurrentlyReadingModal] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteShelf, setDeleteShelf] = useState(null);
  const [error, setError] = useState(null);

  // Configurations
  const limit = 10; // Limit of books per page
  const sort = 'date_updated-DESC'; // Default sort order

  // Function to handle click on view all button
  const handleListViewClick = (shelf) => {
    navigate(`/booknook/${shelf}`);
  };

  // Function to open create modal
  const openCreateModal = () => {
    setDisplayCreateModal(prev => !prev);
  };

  /**
   * Handles opening and closing book details modal
   * @param {*} bookId - id of book to display details for
   */
  const handleBookClick = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayBookDetailsModal(true);
  };

  /**
   * Switch to currently reading view
   */
  const switchToCurrentlyReadingView = () => {
    setDisplayBookDetailsModal(false);
    handleCurrentlyReadingClick(selectedBookId);
  };

  /**
   * Switch to details view
   */
  const switchToDetailsView = () => {
    setDisplayCurrentlyReadingModal(false);
    handleBookClick(selectedBookId);
  };

  /**
   * Handles opening and closing currently reading details modal
   * @param {*} bookId 
   */
  const handleCurrentlyReadingClick = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayCurrentlyReadingModal(true);
  };

  /**
   * Handles opening and closing delete shelf confirmation modal
   * @param {*} shelf_id - id of shelf to delete
   * @param {*} shelf_name - name of shelf to delete
   */
  const handleDeleteShelfConfirmation = (shelf_id, shelf_name) => {
    setOpenConfirmDelete(true);
    setDeleteShelf({id: shelf_id, name: shelf_name});
  };

  /**
   * Handle delete shelf
   * @param {*} deleteShelf - shelf to delete
   */
  const handleDeleteShelf = async (deleteShelf) => {
    try {
      await axios.delete(`${API_BASE_URL}/shelves/${deleteShelf}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError(null);
      fetchCustomShelves();
    } catch (error) {
      setError('An error occurred while deleting shelf.');
    } finally {
      setOpenConfirmDelete(false);
      setDeleteShelf(null);
    }
  };

  /**
   * Refresh shelves
   */
  const refreshShelves = () => {
    fetchShelfBooks();
    fetchCustomShelves();
  };

  /**
   * Fetch books in main shelves
   */
  const fetchShelfBooks = useCallback(async () => {
    MAIN_SHELF_TYPES.forEach(async (shelf) => {
      setShelfLoading((prevState) => ({ ...prevState, [shelf.name]: true }));
      try {
        const response = await axios.get(`${API_BASE_URL}/books/shelf-books`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { sort, offset: 0, limit, shelfName: shelf.name },
        });

        setShelfBooks((prevState) => ({ ...prevState, [shelf.name]: response.data.books }));
      } catch (error) {
        setShelfErrors((prevState) => ({ ...prevState, [shelf.name]: 'An error occurred while fetching books.' }));
      } finally {
        setShelfLoading((prevState) => ({ ...prevState, [shelf.name]: false }));
      }
    });
  }, [token, sort, limit]);

  /**
   * Fetch custom shelves
   */
  const fetchCustomShelves = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shelves/custom`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setError(null);
      setCustomShelves(response.data);
    } catch (error) {
      setError('An error occurred while fetching custom shelves.');
    }
  }, [token]);

  // Fetch books in custom shelves using the /shelf-books endpoint
  const fetchCreatedShelvesBooks = useCallback(async (shelfName) => {
    setCustomShelfLoading((prevState) => ({...prevState, [shelfName]: true}));
    try {
      const response = await axios.get(`${API_BASE_URL}/books/shelf-books`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { sort, offset: 0, limit, shelfName },
      });

      setCustomShelfBooks((prevState) => ({...prevState, [shelfName]: response.data.books}));
    } catch (error) {
      setCustomShelfErrors((prevState) => ({...prevState, [shelfName]: 'An error occurred while fetching books.'}));
    }
    finally {
      setCustomShelfLoading((prevState) => ({...prevState, [shelfName]: false}));
    }
  }, [token, sort, limit]);

  /**
   * Fetch books in shelves on page load
   */
  useEffect(() => {
    fetchShelfBooks();
    fetchCustomShelves();
  }, [token, fetchShelfBooks, fetchCustomShelves]);

  /**
   * Fetch books in custom shelves
   */
  useEffect(() => {
    if (customShelves.length > 0) {
      customShelves.forEach((shelf) => {
        fetchCreatedShelvesBooks(shelf.name);
      });
    }
  }, [customShelves, token, fetchCreatedShelvesBooks]);

  return (
    <div>
      {displayCreateModal && 
        <CreateShelfModal setDisplayCreateModal={setDisplayCreateModal} refreshShelves={fetchCustomShelves}/>}
      <div className="booknook-page">
        
        {/* Main shelves */}
        {MAIN_SHELF_TYPES.map((shelf) => (
          <div className="list-preview-container" key={shelf.id}>
            <div className="list-preview-header">
              <h2>{shelf.name}</h2>
              <button className="link-button" onClick={() => handleListViewClick(shelf.name)}>View All</button>
            </div>
            <div className="list-preview shelf-preview">
              {shelfLoading[shelf.name] ? (
                <p>Loading {shelf.name} books...</p>
              ) : shelfErrors[shelf.name] ? (
                <p className="error">{shelfErrors[shelf.name]}</p>
              ) : (
                shelfBooks[shelf.name] && shelfBooks[shelf.name].length > 0 ? (

                  shelf.name === 'Currently Reading' ? (
                    shelfBooks[shelf.name].map((book, index) => (
                      <CurrentlyReadingDisplay key={index} book={book} onBookClick={handleCurrentlyReadingClick} />
                    ))
                  ) : (
                    shelfBooks[shelf.name].map((book, index) => (
                      <BookDisplay key={index} book={book} onBookClick={handleBookClick} />
                    ))
                  )

                ) : (
                  <p className="no-books-found">No books in <span className="shelf-name">{shelf.name}</span> yet.</p>
                )
              )}
            </div>
          </div>
        ))}

        <hr className="divider" />

        {/* Custom shelves */}
        <div className="create-button-container">
          <button className="tertiary-button" onClick={() => openCreateModal()}>Create New Shelf</button>
        </div>

        {error && <p className="error">{error}</p>}

        {customShelves.length > 0 ? (
          customShelves.map((shelf) => (
            <div className="list-preview-container" key={shelf.id}>
              <div className="list-preview-header">
                <h2>{shelf.name}</h2>
                <button className="link-button" onClick={() => handleListViewClick(shelf.name)}>View All</button>
                <button className="link-button delete-shelf important-button" onClick={() => handleDeleteShelfConfirmation(shelf.id, shelf.name)}>Delete Shelf</button>
              </div>
              <div className="list-preview shelf-preview">
                {customShelfLoading[shelf.name] ? (
                  <p>Loading {shelf.name} books...</p>
                ) : customShelfErrors[shelf.name] ? (
                  <p className="error">{customShelfErrors[shelf.name]}</p>
                ) : (
                  customShelfBooks[shelf.name] && customShelfBooks[shelf.name].length > 0 ? (
                    customShelfBooks[shelf.name].map((book, index) => (
                      <BookDisplay key={index} book={book} onBookClick={handleBookClick} />
                    ))
                  ) : (
                    <p className="no-books-found">No books in <span className="shelf-name">{shelf.name}</span> yet.</p>
                  )
                )}
              </div>
            </div>
          ))
        ) : (
          <p>No custom shelves created yet.</p>
        )}

        {/* Modals */}
        {displayBookDetailsModal && (
          <BookDetailsModal
            setDisplayBookDetailsModal={setDisplayBookDetailsModal}
            bookId={selectedBookId}
            refreshShelves={refreshShelves}
            switchToCurrentlyReadingView={switchToCurrentlyReadingView}
          />
        )}

        {openConfirmDelete && (
          <ConfirmationModal
            onClose={() => setOpenConfirmDelete(false)}
            onConfirm={() => handleDeleteShelf(deleteShelf.id)}
            title={`Are you sure you want to delete "${deleteShelf.name}"?`}
            message={
              "All books in this shelf will be removed. This action cannot be undone."
            }
          />
        )}

        {displayCurrentlyReadingModal && (
          <CurrentlyReadingDetailsModal
            setDisplayCurrentlyReadingModal={setDisplayCurrentlyReadingModal}
            bookId={selectedBookId}
            refreshShelves={refreshShelves}
            switchToDetailsView={switchToDetailsView}
          />
        )}

      </div>
    </div>
  );
}

export default BookNookPage;